import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// DSP colors matching the main trends page
const DSP_COLORS = {
  spotify: "#1DB954",
  applemusic: "#FF0436",
  amazonmusic: "#FF9900",
  youtube: "#FF0000",
  youtubemusic: "#FF0000",
  deezer: "#FEAA2D",
  pandora: "#3668FF",
  soundcloud: "#FF5500"
}

const DSP_NAMES = {
  spotify: "Spotify",
  applemusic: "Apple Music",
  amazonmusic: "Amazon Music",
  youtube: "YouTube",
  youtubemusic: "YouTube Music",
  deezer: "Deezer",
  pandora: "Pandora",
  soundcloud: "SoundCloud"
}

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    streams: Object,
    startDate: String,
    endDate: String,
    dsps: Array,
    emptyMessage: String
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const streamsData = this.streamsValue
    const dsps = this.dspsValue
    const startDate = new Date(this.startDateValue)
    const endDate = new Date(this.endDateValue)

    // Generate date range
    const dates = []
    const currentDate = new Date(startDate)
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // Build series data for each DSP
    const series = dsps.map(dsp => {
      const data = dates.map(date => {
        const dateStr = date.toISOString().split('T')[0]
        // Key format from Rails: ["2024-01-01", "spotify"] => streams
        const key = `${dateStr},${dsp}`
        const streams = streamsData[key] || 0
        return {
          x: date.getTime(),
          y: streams
        }
      })

      return {
        name: DSP_NAMES[dsp] || dsp,
        data: data
      }
    })

    // Get colors for selected DSPs
    const colors = dsps.map(dsp => DSP_COLORS[dsp] || "#6B7280")

    // Check if there's any data
    const hasData = series.some(s => s.data.some(d => d.y > 0))

    if (!hasData) {
      this.showEmptyState()
      return
    }

    // Clear the canvas target
    this.canvasTarget.innerHTML = ''

    const options = {
      series: series,
      chart: {
        type: 'area',
        height: 300,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM dd'
        }
      },
      yaxis: {
        labels: {
          formatter: function(value) {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M'
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'K'
            }
            return Math.floor(value).toLocaleString()
          }
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          const dateTimestamp = w.globals.seriesX[seriesIndex][dataPointIndex]
          const date = new Date(dateTimestamp)

          const formattedDate = date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })

          let tooltipHtml = '<div class="px-3 py-2" style="min-width: 180px;">'
          tooltipHtml += `<div class="font-semibold text-gray-900 mb-2">${formattedDate}</div>`

          let dspRows = []
          w.globals.seriesNames.forEach((name, index) => {
            const value = series[index][dataPointIndex]
            if (value !== undefined && value !== null) {
              dspRows.push({
                name: name,
                value: value,
                color: w.globals.colors[index]
              })
            }
          })

          dspRows.sort((a, b) => b.value - a.value)

          dspRows.forEach(row => {
            tooltipHtml += '<div class="flex items-center justify-between gap-4 py-1">'
            tooltipHtml += `<div class="flex items-center gap-2">`
            tooltipHtml += `<div class="w-3 h-3 rounded-sm" style="background-color: ${row.color};"></div>`
            tooltipHtml += `<span class="text-sm text-gray-700">${row.name}</span>`
            tooltipHtml += `</div>`
            tooltipHtml += `<span class="text-sm font-medium text-gray-900">${row.value.toLocaleString()}</span>`
            tooltipHtml += '</div>'
          })

          const total = dspRows.reduce((sum, row) => sum + row.value, 0)
          tooltipHtml += '<div class="border-t border-gray-200 mt-2 pt-2">'
          tooltipHtml += '<div class="flex items-center justify-between gap-4">'
          tooltipHtml += `<span class="text-sm font-semibold text-gray-900">Total</span>`
          tooltipHtml += `<span class="text-sm font-semibold text-gray-900">${total.toLocaleString()}</span>`
          tooltipHtml += '</div></div></div>'

          return tooltipHtml
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        offsetY: 0
      },
      colors: colors,
      grid: {
        borderColor: '#e7e7e7'
      }
    }

    this.chart = new ApexCharts(this.canvasTarget, options)
    this.chart.render()
  }

  showEmptyState() {
    const message = this.emptyMessageValue || 'No streaming data available for the past 7 days.'
    this.canvasTarget.innerHTML = `<div class="flex items-center justify-center h-full text-gray-500">${message}</div>`
  }
}
